/**
 * Saisie de notes d'examen par CODE DE COPIE (nouveau, 2026-09-02).
 *
 * Contexte : quand un relevé de correction est trop pâle, l'OCR échoue sur
 * les codes-barres. Comme le relevé de correction ne contient QUE le code
 * de copie et la note (anonymat pédagogique), la saisie via matricule/nom
 * n'est pas possible pour l'agent — il n'a pas cette information sous
 * les yeux. Cette page permet de saisir directement par code de copie.
 *
 * Deux modes :
 *  - Liste : tableau des copies notables, input note inline. Tabulation
 *    entre les lignes, sauvegarde auto au blur/Enter.
 *  - Scan : gros input focus permanent pour douchette USB. Scan → focus
 *    saute sur input note → Enter → retour scan.
 *
 * Anonymat : contrôlé par un paramètre global admin. Quand actif, les
 * colonnes matricule/nom/photo sont masquées.
 *
 * Anti-fatigue :
 *  - Grande police, contraste élevé, zébrage doux
 *  - Progression visible en permanence (X/Y saisies + barre)
 *  - Focus ring très visible sur la ligne active
 *  - Auto-sauvegarde avec indicateur discret
 *  - Suggestion de pause de 30s toutes les 25 notes (dismissible)
 *  - Bip sonore optionnel à chaque note validée
 */
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  ScanLine, Keyboard, Search, CheckCircle, XCircle, Loader2,
  AlertCircle, EyeOff, Volume2, VolumeX, Coffee, RefreshCw, X,
  BookOpen, Users, User, History, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';
import { saisieParCodeService, type CopieSaisieRow, type CopiesPourSaisieResponse, type MatiereGroupeeItem, type HistoriqueSaisieItem } from '../../services/api.service';
import { useGetGenerationVerrousQuery } from '../../services/coreApi';

type Mode = 'liste' | 'scan';
type FiltreState = 'toutes' | 'a_saisir' | 'saisies';

// Petit bip synthétisé (WebAudio) — plus propre qu'un fichier
function playBip(freq = 880, duration = 80) {
  try {
    const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine'; o.frequency.value = freq;
    g.gain.value = 0.05;
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + duration / 1000);
  } catch { /* silencieux */ }
}

export default function SaisieParCodeCopie() {
  // Fix 2026-09-02 v2 : matières GROUPÉES (nom+niveau+semestre) — plus une
  // ligne par option comme avant. Le clic charge toutes les copies des
  // matières homonymes en une seule fois.
  const [matieresGroupes, setMatieresGroupes] = useState<MatiereGroupeeItem[]>([]);
  const [matiereGroupeSel, setMatiereGroupeSel] = useState<MatiereGroupeeItem | null>(null);
  const [rechercheMatiere, setRechercheMatiere] = useState('');
  // Fix 2026-09-07 : ouvre le dropdown au clic (sans avoir à taper) + filtre pôle
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filtrePole, setFiltrePole] = useState<string>('');   // '' = tous pôles
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [typeExamen, setTypeExamen] = useState<'NORMAL' | 'RATTRAPAGE'>('NORMAL');
  const [mode, setMode] = useState<Mode>('liste');
  const [filtre, setFiltre] = useState<FiltreState>('toutes');
  const [data, setData] = useState<CopiesPourSaisieResponse | null>(null);
  const [historique, setHistorique] = useState<HistoriqueSaisieItem[]>([]);
  const [showHistorique, setShowHistorique] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [rechercheCode, setRechercheCode] = useState('');
  const [saving, setSaving] = useState<Record<string, 'saving' | 'ok' | 'err'>>({});
  const [bipEnabled, setBipEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem('saisie_code_bip') !== '0'; } catch { return true; }
  });

  // Anti-fatigue : compteur de notes saisies depuis dernière pause
  const [nbSaisiesSession, setNbSaisiesSession] = useState(0);
  const [pauseVisible, setPauseVisible] = useState(false);

  // Verrous globaux : type_examen forcé + parité semestre forcée
  const { data: verrous } = useGetGenerationVerrousQuery();
  const verrouTypeExamen = verrous?.type_examen || '';       // '' | 'normal' | 'rattrapage'
  const verrouSemestreParity = verrous?.semestre_parity || ''; // '' | 'impair' | 'pair'

  // Appliquer le verrou type_examen dès qu'on le connaît
  useEffect(() => {
    if (verrouTypeExamen === 'normal' && typeExamen !== 'NORMAL') {
      setTypeExamen('NORMAL');
    } else if (verrouTypeExamen === 'rattrapage' && typeExamen !== 'RATTRAPAGE') {
      setTypeExamen('RATTRAPAGE');
    }
  }, [verrouTypeExamen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger la liste des matières GROUPÉES (avec parité si verrou actif)
  //
  // Fix 2026-10-02 : les verrous arrivent de façon asynchrone. On partait donc
  // avec `typeExamen = 'NORMAL'` (valeur par défaut), une première requête
  // partait en session ordinaire, puis le verrou basculait sur le rattrapage
  // et une seconde requête partait. Les deux réponses se couraient après : si
  // celle de l'ordinaire arrivait en dernier, la liste affichait SES compteurs
  // — « Well test » montrait 9/9 (les 9 copies de la session ordinaire) au
  // lieu de 0/1 (l'unique copie de rattrapage). On attend donc de connaître
  // les verrous, et toute réponse devenue obsolète est ignorée.
  useEffect(() => {
    if (verrous === undefined) return;
    let obsolete = false;
    const params: any = { type_examen: typeExamen };
    if (verrouSemestreParity === 'impair' || verrouSemestreParity === 'pair') {
      params.parite = verrouSemestreParity;
    }
    saisieParCodeService.listerMatieres(params)
      .then(r => { if (!obsolete) setMatieresGroupes(r.matieres || []); })
      .catch(() => { if (!obsolete) setMatieresGroupes([]); });
    return () => { obsolete = true; };
  }, [typeExamen, verrouSemestreParity, verrous]);

  const recharger = useCallback(async () => {
    if (!matiereGroupeSel) return;
    setLoadingList(true);
    try {
      // Fix 2026-09-07 : propager `enseignant_id` pour ne charger QUE les
      // copies du formateur du groupe sélectionné (une même matière peut
      // être enseignée par 2 formateurs → 2 groupes distincts).
      const ens = matiereGroupeSel.enseignant_id;
      const ensParam: number | 'none' | undefined =
        ens === null || ens === undefined ? 'none' : ens;
      const r = await saisieParCodeService.liste({
        matiere_ids: matiereGroupeSel.matiere_ids.join(','),
        enseignant_id: ensParam,
        type_examen: typeExamen,
      });
      setData(r);
      // Charger l'historique en parallèle
      saisieParCodeService.historique({
        matiere_ids: matiereGroupeSel.matiere_ids.join(','),
        type_examen: typeExamen, limit: 50,
      }).then(h => setHistorique(h.items || [])).catch(() => setHistorique([]));
    } catch (e) {
      setData(null);
    } finally {
      setLoadingList(false);
    }
  }, [matiereGroupeSel, typeExamen]);

  useEffect(() => { recharger(); }, [recharger]);

  // Liste des pôles disponibles pour le filtre (extraits des matières chargées)
  const polesDispo = useMemo(() => {
    const set = new Set<string>();
    matieresGroupes.forEach(m => (m.poles || []).forEach(p => p && set.add(p)));
    return Array.from(set).sort();
  }, [matieresGroupes]);

  const matieresFiltrees = useMemo(() => {
    let liste = matieresGroupes;
    // Filtre pôle (indépendant de la recherche texte)
    if (filtrePole) {
      liste = liste.filter(m => (m.poles || []).includes(filtrePole));
    }
    // Filtre recherche texte (nom/code/classe/enseignant/pôle)
    if (rechercheMatiere) {
      const q = rechercheMatiere.toLowerCase();
      liste = liste.filter(m =>
        m.nom_matiere.toLowerCase().includes(q)
        || m.codes_matieres.some(c => c.toLowerCase().includes(q))
        || m.classes.some(c => c.toLowerCase().includes(q))
        || m.enseignants.some(e => e.toLowerCase().includes(q))
        || (m.poles || []).some(p => p.toLowerCase().includes(q))
      );
    }
    return liste.slice(0, 500);
  }, [matieresGroupes, rechercheMatiere, filtrePole]);

  // Fix 2026-09-07 : fermer le dropdown quand on clique en dehors.
  useEffect(() => {
    if (!dropdownOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [dropdownOpen]);

  const rowsFiltres = useMemo(() => {
    if (!data) return [];
    let rows = data.copies;
    if (filtre === 'a_saisir') rows = rows.filter(c => !c.saisie);
    else if (filtre === 'saisies') rows = rows.filter(c => c.saisie);
    if (rechercheCode) {
      const q = rechercheCode.toLowerCase();
      rows = rows.filter(c =>
        c.code_copie.toLowerCase().includes(q)
        || (c.etudiant_matricule || '').toLowerCase().includes(q)
        || (c.etudiant_nom || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [data, filtre, rechercheCode]);

  // Sauvegarde d'une note pour une copie donnée
  const enregistrer = useCallback(async (code: string, note: number | null, absent: boolean) => {
    setSaving(s => ({ ...s, [code]: 'saving' }));
    try {
      await saisieParCodeService.saisir({
        code_copie: code, note, absent, type_examen: typeExamen,
      });
      setSaving(s => ({ ...s, [code]: 'ok' }));
      if (bipEnabled) playBip(880, 60);
      // Mettre à jour la ligne localement (sans refetch)
      setData(prev => prev ? ({
        ...prev,
        copies: prev.copies.map(c => c.code_copie === code ? {
          ...c,
          note_existante: absent ? null : note,
          absent_existant: absent,
          saisie: absent || note !== null,
          date_saisie: new Date().toISOString(),
        } : c),
        nb_saisies: prev.copies.filter(c => c.code_copie === code
          ? (absent || note !== null)
          : c.saisie
        ).length,
        nb_restantes: prev.copies.filter(c => c.code_copie === code
          ? !(absent || note !== null)
          : !c.saisie
        ).length,
      }) : null);
      // Effacer l'indicateur au bout de 1.5s
      setTimeout(() => setSaving(s => {
        const cp = { ...s }; delete cp[code]; return cp;
      }), 1500);
      // Pause anti-fatigue
      setNbSaisiesSession(n => {
        const nv = n + 1;
        if (nv > 0 && nv % 25 === 0) setPauseVisible(true);
        return nv;
      });
    } catch (e: any) {
      setSaving(s => ({ ...s, [code]: 'err' }));
      if (bipEnabled) playBip(220, 200);
      alert(e?.response?.data?.detail || 'Erreur lors de l\'enregistrement');
      setTimeout(() => setSaving(s => {
        const cp = { ...s }; delete cp[code]; return cp;
      }), 3000);
    }
  }, [typeExamen, bipEnabled]);

  const matiereSel = matiereGroupeSel;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-950">
      {/* ── HEADER ── */}
      <header className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-lg font-bold text-slate-900 dark:text-white inline-flex items-center gap-2">
                <ScanLine size={18} className="text-blue-600" />
                Saisie par code de copie
              </h1>
              <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                Pour les relevés de correction dont l'OCR ne lit pas les codes-barres.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Badge indicateur — l'anonymat se gère dans Paramètres > Général */}
              {data?.anonymat_actif && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-[11px] font-semibold">
                  <EyeOff size={11} /> Anonymat actif
                </span>
              )}
              <button
                onClick={() => {
                  const next = !bipEnabled;
                  setBipEnabled(next);
                  try { localStorage.setItem('saisie_code_bip', next ? '1' : '0'); } catch {}
                }}
                title={bipEnabled ? 'Désactiver le bip' : 'Activer le bip'}
                className="p-1.5 rounded border border-slate-200 dark:border-gray-700 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800"
              >
                {bipEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
              <button
                onClick={recharger}
                disabled={loadingList || !matiereGroupeSel}
                title="Recharger la liste"
                className="p-1.5 rounded border border-slate-200 dark:border-gray-700 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 disabled:opacity-40"
              >
                <RefreshCw size={14} className={loadingList ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* ── PANNEAU DE SÉLECTION MATIÈRE (z-30 pour passer au-dessus des zones vides) ── */}
        <section className="relative z-30 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 dark:text-gray-400 font-semibold mb-1">
                Matière
                {verrouSemestreParity && (
                  <span className="ml-1.5 inline-flex items-center gap-0.5 text-red-600 text-[9px]">
                    Verrou : semestres {verrouSemestreParity === 'impair' ? 'impairs (S1/S3/S5)' : 'pairs (S2/S4/S6)'} uniquement
                  </span>
                )}
              </label>
              <div className="relative" ref={dropdownRef}>
                <Search size={12} className="absolute left-2 top-2 text-slate-400" />
                <input
                  type="text"
                  value={rechercheMatiere || (dropdownOpen ? '' : (matiereSel ? `${matiereSel.nom_matiere} · ${matiereSel.niveau}${matiereSel.enseignant_nom ? ' — ' + matiereSel.enseignant_nom : ''}` : ''))}
                  onChange={(e) => { setRechercheMatiere(e.target.value); setMatiereGroupeSel(null); setDropdownOpen(true); }}
                  onFocus={() => setDropdownOpen(true)}
                  onClick={() => setDropdownOpen(true)}
                  placeholder="Cliquez pour ouvrir la liste, ou tapez pour filtrer…"
                  className="w-full pl-7 pr-8 py-1.5 text-xs bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none cursor-pointer"
                />
                <button
                  type="button"
                  onClick={() => setDropdownOpen(o => !o)}
                  className="absolute right-1.5 top-1 p-1 text-slate-400 hover:text-slate-700 dark:hover:text-gray-200"
                  aria-label={dropdownOpen ? 'Fermer' : 'Ouvrir la liste'}
                >
                  {dropdownOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </button>
                {dropdownOpen && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg shadow-2xl max-h-[420px] overflow-hidden flex flex-col">
                    {/* Fix 2026-09-07 : barre filtre pôle + compteur */}
                    {polesDispo.length > 0 && (
                      <div className="flex-shrink-0 px-2 pt-2 pb-1.5 border-b border-slate-100 dark:border-gray-800 bg-slate-50 dark:bg-gray-950 flex items-center gap-1.5 overflow-x-auto">
                        <span className="text-[9px] uppercase font-semibold text-slate-500 dark:text-gray-400 flex-shrink-0 mr-1">
                          Pôle :
                        </span>
                        <button
                          type="button"
                          onClick={() => setFiltrePole('')}
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-colors flex-shrink-0 ${
                            filtrePole === ''
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-700 dark:text-gray-300 hover:border-blue-500'
                          }`}
                        >
                          Tous ({matieresGroupes.length})
                        </button>
                        {polesDispo.map(p => {
                          const nb = matieresGroupes.filter(m => (m.poles || []).includes(p)).length;
                          return (
                            <button key={p}
                              type="button"
                              onClick={() => setFiltrePole(p)}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-colors flex-shrink-0 ${
                                filtrePole === p
                                  ? 'bg-blue-600 border-blue-600 text-white'
                                  : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-700 dark:text-gray-300 hover:border-blue-500'
                              }`}
                            >
                              {p} ({nb})
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="overflow-y-auto flex-1">
                      {matieresFiltrees.length === 0 ? (
                        <div className="px-3 py-4 text-center text-[11px] text-slate-500">
                          Aucune matière {filtrePole && `dans le pôle « ${filtrePole} »`}
                          {verrouSemestreParity && ` (verrou : semestres ${verrouSemestreParity}s)`}
                        </div>
                      ) : (
                        matieresFiltrees.map(m => (
                          <button key={m.cle}
                            onClick={() => {
                              setMatiereGroupeSel(m);
                              setRechercheMatiere('');
                              setDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-gray-800 border-b border-slate-100 dark:border-gray-800 last:border-b-0 ${
                              matiereGroupeSel?.cle === m.cle ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                          >
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs font-semibold text-slate-800 dark:text-gray-200 truncate">{m.nom_matiere}</span>
                              <span className="text-[10px] text-slate-500 flex-shrink-0">{m.niveau} · {m.semestre_absolu}</span>
                              <span className="ml-auto text-[10px] text-slate-400 flex-shrink-0">
                                {m.nb_notes_saisies}/{m.nb_copies_total}
                              </span>
                            </div>
                            {m.enseignant_nom && (
                              <div className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50 text-[10px] font-semibold">
                                <User size={9} /> {m.enseignant_nom}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500 flex-wrap">
                              {(m.poles || []).length > 0 && (
                                <span className="inline-flex items-center px-1 py-0.5 rounded bg-slate-100 dark:bg-gray-800 text-[9px] font-semibold text-slate-600 dark:text-gray-400">
                                  {(m.poles || []).join(' / ')}
                                </span>
                              )}
                              {m.classes.length > 0 && (
                                <span className="inline-flex items-center gap-0.5 truncate flex-1">
                                  <Users size={9} /> {m.classes.join(', ')}
                                </span>
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-slate-500 dark:text-gray-400 font-semibold mb-1">
                Session
                {verrouTypeExamen && (
                  <span className="ml-1.5 text-red-600 text-[9px]">Verrouillée</span>
                )}
              </label>
              <div className="inline-flex bg-slate-100 dark:bg-gray-800 rounded-lg p-0.5 w-full">
                {(['NORMAL', 'RATTRAPAGE'] as const).map(t => {
                  const isDisabled = (t === 'NORMAL' && verrouTypeExamen === 'rattrapage')
                    || (t === 'RATTRAPAGE' && verrouTypeExamen === 'normal');
                  return (
                    <button key={t}
                      onClick={() => !isDisabled && setTypeExamen(t)}
                      disabled={isDisabled}
                      className={`flex-1 py-1.5 text-[11px] font-semibold rounded-md transition ${
                        typeExamen === t
                          ? 'bg-white dark:bg-gray-700 shadow text-slate-900 dark:text-white'
                          : isDisabled
                            ? 'text-slate-300 dark:text-gray-600 cursor-not-allowed'
                            : 'text-slate-600 dark:text-gray-400 hover:text-slate-800'
                      }`}
                    >
                      {t === 'NORMAL' ? 'Ordinaire' : 'Rattrapage'}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {matiereSel && data && (
            <div className="mt-2.5 space-y-1.5">
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="text-slate-500 dark:text-gray-400">Contexte :</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 font-medium">
                  {data.matiere.niveau}
                </span>
                <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 font-medium">
                  {data.annee_academique} · {data.semestre}
                </span>
                {data.codes_matieres.length > 1 && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">
                    {data.codes_matieres.length} options regroupées
                  </span>
                )}
                {/* Fix 2026-09-07 : badge « Relevé de X » — bien mettre en
                    évidence que la saisie ne concerne QUE les codes de ce
                    formateur, pas ceux de son collègue sur la même matière. */}
                {data.enseignant_nom && (
                  <span className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 font-semibold border border-amber-300 dark:border-amber-700 inline-flex items-center gap-1">
                    <User size={10} /> Relevé de {data.enseignant_nom}
                  </span>
                )}
              </div>
              {/* Enseignants */}
              {data.enseignants.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className="text-slate-500 dark:text-gray-400 inline-flex items-center gap-1">
                    <User size={10} /> Enseignant{data.enseignants.length > 1 ? 's' : ''} :
                  </span>
                  {data.enseignants.map((n, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 font-medium border border-indigo-200 dark:border-indigo-800">
                      {n}
                    </span>
                  ))}
                </div>
              )}
              {/* Classes */}
              {data.classes_concernees.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className="text-slate-500 dark:text-gray-400 inline-flex items-center gap-1">
                    <Users size={10} /> Classes concernées :
                  </span>
                  {data.classes_concernees.map(c => (
                    <span key={c} className="px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-mono font-semibold border border-emerald-200 dark:border-emerald-800">
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {!matiereGroupeSel && (
          <div className="bg-white dark:bg-gray-900 border border-dashed border-slate-300 dark:border-gray-800 rounded-lg p-12 text-center">
            <ScanLine size={40} className="inline text-slate-300 mb-3" />
            <div className="text-slate-600 dark:text-gray-400 font-medium">
              Sélectionnez d'abord une matière pour commencer.
            </div>
          </div>
        )}

        {/* ── PROGRESSION + MODES ── */}
        {matiereGroupeSel && data && (
          <>
            <ProgressionBar data={data} />

            <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex bg-slate-100 dark:bg-gray-800 rounded-lg p-0.5">
                  <button
                    onClick={() => setMode('liste')}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                      mode === 'liste'
                        ? 'bg-white dark:bg-gray-700 shadow text-slate-900 dark:text-white'
                        : 'text-slate-600 dark:text-gray-400'
                    }`}
                  >
                    <Keyboard size={13} /> Mode Liste
                  </button>
                  <button
                    onClick={() => setMode('scan')}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition ${
                      mode === 'scan'
                        ? 'bg-white dark:bg-gray-700 shadow text-slate-900 dark:text-white'
                        : 'text-slate-600 dark:text-gray-400'
                    }`}
                  >
                    <ScanLine size={13} /> Mode Scan
                  </button>
                </div>

                <div className="inline-flex bg-slate-100 dark:bg-gray-800 rounded-lg p-0.5">
                  {(['toutes', 'a_saisir', 'saisies'] as const).map(f => (
                    <button key={f}
                      onClick={() => setFiltre(f)}
                      className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition ${
                        filtre === f
                          ? 'bg-white dark:bg-gray-700 shadow text-slate-900 dark:text-white'
                          : 'text-slate-600 dark:text-gray-400'
                      }`}
                    >
                      {f === 'toutes' ? 'Toutes' : f === 'a_saisir' ? 'À saisir' : 'Saisies'}
                    </button>
                  ))}
                </div>

                <div className="relative flex-1 min-w-[200px] max-w-xs">
                  <Search size={13} className="absolute left-2 top-2 text-slate-400" />
                  <input
                    type="text"
                    value={rechercheCode}
                    onChange={(e) => setRechercheCode(e.target.value)}
                    placeholder={data.anonymat_actif ? "Chercher un code..." : "Chercher code, nom, matricule..."}
                    className="w-full pl-7 pr-2 py-1.5 text-xs bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded"
                  />
                  {rechercheCode && (
                    <button onClick={() => setRechercheCode('')}
                      className="absolute right-1 top-1 p-0.5 text-slate-400 hover:text-slate-700">
                      <X size={11} />
                    </button>
                  )}
                </div>
              </div>
            </section>

            {loadingList && (
              <div className="text-center py-8 text-slate-500 text-sm">
                <Loader2 size={16} className="inline animate-spin mr-2" /> Chargement des copies…
              </div>
            )}

            {!loadingList && mode === 'liste' && (
              <ModeListe rows={rowsFiltres} anonymat={data.anonymat_actif}
                saving={saving} onSave={enregistrer} />
            )}
            {!loadingList && mode === 'scan' && (
              <ModeScan rows={rowsFiltres} allRows={data.copies}
                anonymat={data.anonymat_actif}
                saving={saving} onSave={enregistrer} />
            )}

            {/* ── HISTORIQUE DE SAISIE (audit) ── */}
            <HistoriquePanel
              items={historique}
              expanded={showHistorique}
              onToggle={() => setShowHistorique(v => !v)}
              onRefresh={recharger}
            />
          </>
        )}
      </main>

      {/* Modale pause anti-fatigue */}
      {pauseVisible && (
        <PauseModal onClose={() => setPauseVisible(false)} nbSaisies={nbSaisiesSession} />
      )}
    </div>
  );
}

// ─── PROGRESSION ────────────────────────────────────────────────────────────

function ProgressionBar({ data }: { data: CopiesPourSaisieResponse }) {
  const pct = data.total > 0 ? Math.round((data.nb_saisies / data.total) * 100) : 0;
  return (
    <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-sm font-semibold text-slate-700 dark:text-gray-300">
          Progression
        </div>
        <div className="text-sm">
          <span className="font-bold text-blue-700 dark:text-blue-400 tabular-nums">{data.nb_saisies}</span>
          <span className="text-slate-400"> / {data.total} copies</span>
          <span className="ml-3 text-emerald-600 font-semibold">{pct}%</span>
        </div>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
        <span>{data.nb_restantes} restante{data.nb_restantes > 1 ? 's' : ''}</span>
        {data.nb_restantes === 0 && data.total > 0 && (
          <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
            <CheckCircle size={11} /> Terminé
          </span>
        )}
      </div>
    </section>
  );
}

// ─── MODE LISTE ─────────────────────────────────────────────────────────────

function ModeListe({ rows, anonymat, saving, onSave }: {
  rows: CopieSaisieRow[]; anonymat: boolean;
  saving: Record<string, string>;
  onSave: (code: string, note: number | null, absent: boolean) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-dashed border-slate-300 dark:border-gray-800 rounded-lg p-8 text-center text-slate-500 text-sm">
        Aucune copie à afficher avec ces filtres.
      </div>
    );
  }
  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-gray-800/60 text-[11px] uppercase tracking-wider text-slate-600 dark:text-gray-400">
            <tr>
              <th className="text-left px-4 py-2.5">Code copie</th>
              {!anonymat && <th className="text-left px-3 py-2.5">Étudiant</th>}
              <th className="text-center px-3 py-2.5 w-40">Note /20</th>
              <th className="text-center px-2 py-2.5 w-24">Absent</th>
              <th className="text-center px-3 py-2.5 w-32">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, idx) => (
              <LigneSaisie key={c.code_copie} c={c} anonymat={anonymat}
                onSave={onSave} savingState={saving[c.code_copie]}
                striped={idx % 2 === 0} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LigneSaisie({ c, anonymat, onSave, savingState, striped }: {
  c: CopieSaisieRow; anonymat: boolean;
  onSave: (code: string, note: number | null, absent: boolean) => void;
  savingState?: string; striped: boolean;
}) {
  const [note, setNote] = useState<string>(
    c.note_existante !== null ? String(c.note_existante) : ''
  );
  const [absent, setAbsent] = useState<boolean>(c.absent_existant);
  const [focused, setFocused] = useState(false);

  // Re-sync si la data côté parent change (recharge)
  useEffect(() => {
    setNote(c.note_existante !== null ? String(c.note_existante) : '');
    setAbsent(c.absent_existant);
  }, [c.note_existante, c.absent_existant]);

  const commit = () => {
    if (absent) {
      onSave(c.code_copie, null, true);
      return;
    }
    const n = note.trim().replace(',', '.');
    if (n === '') return;
    const val = parseFloat(n);
    if (isNaN(val) || val < 0 || val > 20) {
      alert('Note invalide : entre 0 et 20');
      return;
    }
    onSave(c.code_copie, val, false);
  };

  const toggleAbsent = () => {
    const newAbsent = !absent;
    setAbsent(newAbsent);
    onSave(c.code_copie, newAbsent ? null : (parseFloat(note.replace(',', '.')) || null), newAbsent);
  };

  return (
    <tr className={`border-t border-slate-100 dark:border-gray-800 ${
      focused ? 'bg-blue-50/60 dark:bg-blue-900/10' :
      c.saisie ? 'bg-emerald-50/30 dark:bg-emerald-900/10' :
      striped ? 'bg-white dark:bg-gray-900' : 'bg-slate-50/40 dark:bg-gray-800/20'
    } transition-colors`}>
      <td className="px-4 py-2.5">
        <span className="font-mono text-sm font-semibold text-slate-800 dark:text-gray-100">
          {c.code_copie}
        </span>
      </td>
      {!anonymat && (
        <td className="px-3 py-2.5">
          <div className="text-sm text-slate-800 dark:text-gray-100 flex items-center gap-1.5 flex-wrap">
            <span className="truncate">{c.etudiant_nom || '—'}</span>
            {/* Fix 2026-09-07 : classe pour vérification visuelle */}
            {c.etudiant_classe && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50 text-[9px] font-bold font-mono flex-shrink-0">
                {c.etudiant_classe}
              </span>
            )}
          </div>
          <div className="text-[10px] font-mono text-slate-500">{c.etudiant_matricule || ''}</div>
        </td>
      )}
      <td className="px-3 py-2 text-center">
        <input
          type="text"
          inputMode="decimal"
          value={absent ? '' : note}
          disabled={absent}
          onChange={(e) => setNote(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); commit(); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              (e.target as HTMLInputElement).blur();
              // Passer au prochain input
              const next = (e.target as HTMLInputElement)
                .closest('tr')?.nextElementSibling
                ?.querySelector('input[type=text]') as HTMLInputElement | null;
              next?.focus();
            }
          }}
          placeholder={absent ? '—' : '0-20'}
          className={`w-24 text-center py-2 text-lg font-bold rounded-lg border-2 tabular-nums transition ${
            focused
              ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800 bg-white dark:bg-gray-800'
              : c.saisie
                ? 'border-emerald-300 bg-white dark:bg-gray-800 text-emerald-700 dark:text-emerald-400'
                : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          } disabled:opacity-40 disabled:bg-slate-100`}
        />
      </td>
      <td className="px-2 py-2 text-center">
        <label className="inline-flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            checked={absent}
            onChange={toggleAbsent}
            className="w-4 h-4 accent-red-600"
          />
          <span className="text-[11px] text-slate-600 dark:text-gray-400">ABS</span>
        </label>
      </td>
      <td className="px-3 py-2 text-center">
        {savingState === 'saving' && (
          <span className="inline-flex items-center gap-1 text-[11px] text-blue-600">
            <Loader2 size={11} className="animate-spin" /> Enregistrement…
          </span>
        )}
        {savingState === 'ok' && (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-semibold">
            <CheckCircle size={11} /> Enregistré
          </span>
        )}
        {savingState === 'err' && (
          <span className="inline-flex items-center gap-1 text-[11px] text-red-600 font-semibold">
            <XCircle size={11} /> Erreur
          </span>
        )}
        {!savingState && c.saisie && (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
            <CheckCircle size={11} className="text-emerald-500" /> Saisie
          </span>
        )}
        {!savingState && !c.saisie && (
          <span className="text-[11px] text-slate-400">À saisir</span>
        )}
      </td>
    </tr>
  );
}

// ─── MODE SCAN ──────────────────────────────────────────────────────────────

function ModeScan({ rows, allRows, anonymat, saving, onSave }: {
  rows: CopieSaisieRow[]; allRows: CopieSaisieRow[]; anonymat: boolean;
  saving: Record<string, string>;
  onSave: (code: string, note: number | null, absent: boolean) => void;
}) {
  const [codeInput, setCodeInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [copieActive, setCopieActive] = useState<CopieSaisieRow | null>(null);
  const [message, setMessage] = useState<{ type: 'info' | 'err'; text: string } | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<any>(null);

  // Auto-focus le champ code au montage
  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  const chercherCopie = useCallback((code: string) => {
    const trimmed = code.trim();
    if (!trimmed) return;
    const c = allRows.find(r => r.code_copie.toLowerCase() === trimmed.toLowerCase());
    if (!c) {
      setMessage({ type: 'err', text: `Code « ${trimmed} » introuvable dans cette matière/session.` });
      setCopieActive(null);
      setCodeInput('');
      setTimeout(() => codeRef.current?.focus(), 100);
      return;
    }
    setCopieActive(c);
    setNoteInput(c.note_existante !== null ? String(c.note_existante) : '');
    setMessage(null);
    setTimeout(() => noteRef.current?.focus(), 50);
  }, [allRows]);

  // ── Auto-détection scan : le scanner USB envoie tous les caractères d'un
  // coup + éventuellement Enter. On déclenche automatiquement dès que :
  //  (a) le code tapé matche exactement un code existant (match immédiat)
  //  (b) sinon, après 350ms sans nouvelle frappe (debounce, cas non-Enter)
  useEffect(() => {
    if (!codeInput.trim() || copieActive) return;
    const trimmed = codeInput.trim();
    // (a) Match exact → immédiat
    const exact = allRows.find(r => r.code_copie.toLowerCase() === trimmed.toLowerCase());
    if (exact) {
      chercherCopie(trimmed);
      return;
    }
    // (b) Debounce
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Si le user est toujours en train de taper (input focus) et que la string
      // fait au moins 4 caractères → tentative de recherche (peut afficher "introuvable")
      if (trimmed.length >= 4) chercherCopie(trimmed);
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [codeInput, allRows, copieActive, chercherCopie]);

  const enregistrerScan = (absent: boolean = false) => {
    if (!copieActive) return;
    if (absent) {
      onSave(copieActive.code_copie, null, true);
    } else {
      const n = noteInput.trim().replace(',', '.');
      if (n === '') {
        setMessage({ type: 'err', text: 'Saisissez une note avant de valider.' });
        return;
      }
      const val = parseFloat(n);
      if (isNaN(val) || val < 0 || val > 20) {
        setMessage({ type: 'err', text: 'Note invalide : entre 0 et 20' });
        return;
      }
      onSave(copieActive.code_copie, val, false);
    }
    setMessage({ type: 'info', text: `${copieActive.code_copie} enregistrée. En attente de la copie suivante…` });
    setCodeInput(''); setNoteInput(''); setCopieActive(null);
    setTimeout(() => codeRef.current?.focus(), 50);
  };

  const progressionRow = rows.length > 0 ? `${allRows.length - rows.filter(r => !r.saisie).length}/${allRows.length}` : '';

  return (
    <div className="space-y-3">
      {/* Zone scan — grand cadre */}
      <div className={`bg-white dark:bg-gray-900 border-2 rounded-xl p-6 transition-colors ${
        copieActive ? 'border-blue-500' : 'border-slate-200 dark:border-gray-800'
      }`}>
        <div className="max-w-xl mx-auto space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
              <span className="inline-flex items-center gap-1">
                <ScanLine size={12} /> Code de la copie {copieActive ? '✓' : ''}
              </span>
            </label>
            <input
              ref={codeRef}
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && codeInput.trim()) {
                  e.preventDefault();
                  chercherCopie(codeInput.trim());
                }
              }}
              placeholder="Scannez ou tapez le code (détection auto)…"
              className="w-full px-3 py-3 text-lg font-mono font-bold text-center bg-slate-50 dark:bg-gray-800 border-2 border-slate-300 dark:border-gray-700 rounded-lg text-slate-900 dark:text-gray-100 focus:ring-4 focus:ring-blue-200 focus:border-blue-500 focus:outline-none tracking-wider"
            />
          </div>

          {copieActive && !anonymat && (
            <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-gray-700 flex items-center justify-center text-slate-600 dark:text-gray-400 text-sm font-bold">
                {(copieActive.etudiant_nom || '?').split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800 dark:text-gray-200 flex items-center gap-1.5 flex-wrap">
                  <span className="truncate">{copieActive.etudiant_nom}</span>
                  {/* Fix 2026-09-07 : classe pour vérification visuelle */}
                  {copieActive.etudiant_classe && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50 text-[10px] font-bold font-mono flex-shrink-0">
                      {copieActive.etudiant_classe}
                    </span>
                  )}
                </div>
                <div className="text-[11px] font-mono text-slate-500">
                  {copieActive.etudiant_matricule}
                </div>
              </div>
              {copieActive.saisie && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-semibold">
                  Note existante : {copieActive.note_existante ?? 'ABS'}
                </span>
              )}
            </div>
          )}

          {copieActive && (
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                Note /20
              </label>
              <div className="flex gap-2">
                <input
                  ref={noteRef}
                  type="text"
                  inputMode="decimal"
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      enregistrerScan(false);
                    }
                  }}
                  placeholder="0 — 20"
                  className="flex-1 px-3 py-3 text-2xl font-bold text-center bg-white dark:bg-gray-800 border-2 border-blue-400 rounded-lg text-slate-900 dark:text-gray-100 focus:ring-4 focus:ring-blue-200 focus:outline-none tabular-nums"
                />
                <button
                  onClick={() => enregistrerScan(true)}
                  className="px-4 py-2 text-xs font-semibold text-red-700 border-2 border-red-300 rounded-lg hover:bg-red-50 whitespace-nowrap"
                >
                  Absent
                </button>
                <button
                  onClick={() => enregistrerScan(false)}
                  className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  Valider
                </button>
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5">
                Tapez la note puis Entrée. Ou cliquez « Absent ».
              </div>
            </div>
          )}

          {message && (
            <div className={`px-3 py-2 rounded-lg text-sm flex items-start gap-2 ${
              message.type === 'err'
                ? 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                : 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
            }`}>
              {message.type === 'err' ? <AlertCircle size={14} className="mt-0.5" /> : <CheckCircle size={14} className="mt-0.5" />}
              <div className="flex-1">{message.text}</div>
            </div>
          )}
        </div>
      </div>

      {/* Récap récent (dernières saisies) */}
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Dernières saisies ({progressionRow})
        </div>
        <div className="flex flex-wrap gap-1.5">
          {allRows.filter(r => r.saisie).slice(-15).reverse().map(r => (
            <span key={r.code_copie}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-[11px] font-mono">
              {r.code_copie}
              <span className="text-slate-500">·</span>
              <span className="font-bold">{r.absent_existant ? 'ABS' : r.note_existante}</span>
            </span>
          ))}
          {allRows.filter(r => r.saisie).length === 0 && (
            <span className="text-xs text-slate-400 italic">Aucune saisie encore.</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PAUSE MODAL ────────────────────────────────────────────────────────────

function PauseModal({ onClose, nbSaisies }: { onClose: () => void; nbSaisies: number }) {
  const [reste, setReste] = useState(30);
  useEffect(() => {
    const t = setInterval(() => setReste(r => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-2xl max-w-md w-full overflow-hidden">
        <div className="p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-3">
            <Coffee size={28} className="text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
            Pause suggérée
          </h3>
          <p className="text-sm text-slate-600 dark:text-gray-400 mb-4">
            Vous avez saisi <strong>{nbSaisies}</strong> notes de suite. Une petite
            pause de 30 secondes améliore la précision et réduit la fatigue oculaire.
          </p>
          <div className="text-5xl font-bold text-amber-600 tabular-nums mb-3">
            {reste}<span className="text-lg text-slate-400"> s</span>
          </div>
          <div className="text-[11px] text-slate-500 mb-4">
            Fermez les yeux, regardez au loin, respirez profondément.
          </div>
          <button
            onClick={onClose}
            className="w-full py-2.5 text-sm font-semibold bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700 text-slate-700 dark:text-gray-300 rounded-lg"
          >
            {reste > 0 ? 'Reprendre maintenant' : 'C\'est reparti !'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── HISTORIQUE (audit) ─────────────────────────────────────────────────────

function HistoriquePanel({ items, expanded, onToggle, onRefresh }: {
  items: HistoriqueSaisieItem[];
  expanded: boolean;
  onToggle: () => void;
  onRefresh: () => void;
}) {
  const fmtDate = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };
  const badgeRole: Record<string, string> = {
    admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    super_admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    agent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    directeur: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    formateur: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  };
  return (
    <section className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-gray-800/60"
      >
        <span className="text-[11px] uppercase tracking-wider font-bold text-slate-600 dark:text-gray-400 inline-flex items-center gap-1.5">
          <History size={12} /> Historique de saisie
          <span className="text-slate-400 dark:text-gray-500 font-normal normal-case">
            ({items.length} action{items.length > 1 ? 's' : ''} · audit trail)
          </span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-gray-200"
            title="Rafraîchir l'historique"
          >
            <RefreshCw size={12} />
          </button>
          <span className="text-slate-400">{expanded ? '▲' : '▼'}</span>
        </span>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 dark:border-gray-800">
          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-slate-500">
              Aucune saisie enregistrée pour cette matière.
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-gray-800/60 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Code copie</th>
                    <th className="text-center px-2 py-2">Note</th>
                    <th className="text-left px-3 py-2">Agent</th>
                    <th className="text-left px-3 py-2">Action</th>
                    <th className="text-right px-3 py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((h, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/40">
                      <td className="px-3 py-2 font-mono font-semibold text-slate-800 dark:text-gray-100">
                        {h.code_copie}
                      </td>
                      <td className="text-center px-2 py-2">
                        {h.absent ? (
                          <span className="text-red-600 font-semibold">ABS</span>
                        ) : h.note !== null ? (
                          <span className={`font-bold ${h.note >= 10 ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {h.note.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-800 dark:text-gray-200">{h.agent_nom}</div>
                        {h.agent_role && (
                          <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold ${
                            badgeRole[h.agent_role] || 'bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {h.agent_role}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600 dark:text-gray-400">
                        {h.action === 'creation' ? (
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle size={10} className="text-emerald-500" /> Création
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <RefreshCw size={10} className="text-blue-500" /> Modification
                          </span>
                        )}
                      </td>
                      <td className="text-right px-3 py-2 text-slate-500 dark:text-gray-500 tabular-nums">
                        <Clock size={10} className="inline mr-1" />
                        {fmtDate(h.date)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
